import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search, Download, Filter, Plus, Users2, CheckCircle2, Wifi, Clock, MinusCircle, XCircle, FlaskConical, Snowflake,
} from "lucide-react";
import { adminApi } from "./api";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { ROLE_STYLE, StatusCell, VerificationCell, Stars, money, timeAgo, UserAvatar } from "./userCells";
import UserActionsMenu from "./UserActionsMenu";
import { UserProfileDialog, AddUserDialog, AddNoteDialog, SendMessageDialog, KycDialog, UserTripsDialog, UserWalletDialog } from "./UserDialogs";
import { isSeen, markSeen, useSeenVersion } from "./seen";

const PAGE_SIZES = [10, 25, 50];

const STAT_CARDS = [
  { key: "total", label: "Total Users", icon: Users2, color: "#2340b5", sub: "All time" },
  { key: "active", label: "Active Users", icon: CheckCircle2, color: "#16a34a", pct: "active_pct" },
  { key: "online", label: "Online Now", icon: Wifi, color: "#16a34a", sub: "Live" },
  { key: "pending", label: "Pending Verification", icon: Clock, color: "#f59e0b", pct: "pending_pct" },
  { key: "suspended", label: "Suspended", icon: MinusCircle, color: "#e4002b", pct: "suspended_pct" },
  { key: "banned", label: "Banned", icon: XCircle, color: "#e4002b", pct: "banned_pct" },
];

const emptyFilters = { role: "", status: "", verification: "", province: "", joined: "" };
// Default view hides banned/suspended accounts by showing active users only.
const defaultFilters = { ...emptyFilters, status: "active" };

export default function UserManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ ...defaultFilters, verification: searchParams.get("verification") || "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selected, setSelected] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [profileUser, setProfileUser] = useState(null);
  const [noteUser, setNoteUser] = useState(null);
  const [messageUser, setMessageUser] = useState(null);
  const [kycUser, setKycUser] = useState(null);
  const [tripsUser, setTripsUser] = useState(null);
  const [walletUser, setWalletUser] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showTest, setShowTest] = useState(false);
  useSeenVersion(); // re-render when a verification is marked seen
  const openKyc = (u) => { markSeen("verifications", u.id); setKycUser(u); };

  const loadStats = useCallback(() => adminApi.get("/users/stats").then((r) => { setStats(r.data); }).catch(() => setLoadError(true)), []);
  const loadUsers = useCallback(() => {
    setLoadError(false);
    adminApi.get("/users", { params: { search, ...filters, page, limit, include_test: showTest } }).then((r) => {
      setData(r.data);
      setSelected([]);
    }).catch(() => setLoadError(true));
  }, [search, filters, page, limit, showTest]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Keep the verification filter in sync with the URL (?verification=pending shortcut)
  useEffect(() => {
    const v = searchParams.get("verification") || "";
    setFilters((f) => (f.verification === v ? f : { ...f, verification: v }));
    setPage(1);
  }, [searchParams]);

  const togglePendingFilter = () => {
    const next = filters.verification === "pending" ? "" : "pending";
    setPage(1);
    setFilters((f) => ({ ...f, verification: next }));
    setSearchParams(next ? { verification: next } : {});
  };

  const refresh = () => { loadUsers(); loadStats(); };

  const setFilter = (k) => (e) => { setPage(1); setFilters((f) => ({ ...f, [k]: e.target.value })); };
  const clearFilters = () => { setFilters(defaultFilters); setSearch(""); setPage(1); setSearchParams({}); };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === data.items.length ? [] : data.items.map((u) => u.id)));

  const bulk = async (status, label) => {
    if (selected.length === 0) return toast.error("No users selected");
    await Promise.all(selected.map((id) => adminApi.patch(`/users/${id}`, { status })));
    toast.success(`${selected.length} user(s) ${label}`);
    refresh();
  };

  const bulkTest = async (isTest) => {
    if (selected.length === 0) return toast.error("No users selected");
    await Promise.all(selected.map((id) => adminApi.post(`/users/${id}/test-account`, { is_test: isTest })));
    toast.success(isTest
      ? `${selected.length} user(s) marked as test — now hidden from all stats`
      : `${selected.length} user(s) unmarked as test`);
    refresh();
  };

  const exportCsv = () => {
    const rows = [["Name", "User ID", "Email", "Phone", "Role", "Status", "Province", "Trips", "Rating", "Wallet"]];
    data.items.forEach((u) => rows.push([u.name, u.user_code, u.email, u.phone, u.role, u.status,
      u.province, u.trips_completed, u.rating ?? "", u.wallet]));
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "saferide-users.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported current page to CSV");
  };

  const live = !!data.live;
  const sel = "rounded-xl border border-[#12142b]/12 bg-white px-3 py-2.5 text-sm text-[#12142b] outline-none focus:border-[#2340b5]";

  const statCards = STAT_CARDS.filter((c) => !live || c.key !== "online");
  const colCount = live ? 8 : 11;

  const pageNumbers = () => {
    const p = data.page, tot = data.pages;
    const arr = [];
    for (let i = Math.max(1, p - 1); i <= Math.min(tot, p + 1); i++) arr.push(i);
    return arr;
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title="User Management"
        subtitle="Manage riders, drivers, businesses and administrators."
        right={
          <div className="flex items-center gap-2">
            <button
              data-testid="toggle-test-accounts"
              onClick={() => { setPage(1); setShowTest((v) => !v); }}
              className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${showTest ? "border-[#f59e0b] bg-[#f59e0b]/10 text-[#b45309]" : "border-[#12142b]/12 bg-white text-[#12142b] hover:bg-[#f4f6fb]"}`}
              title="Show accounts marked as test (hidden from all stats by default)"
            >
              <FlaskConical className="h-4 w-4" /> {showTest ? "Showing test" : "Show test accounts"}
            </button>
            <button data-testid="export-btn" onClick={exportCsv} className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">
              <Download className="h-4 w-4" /> Export
            </button>
            <button data-testid="filters-btn" onClick={() => setShowFilters((v) => !v)} className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">
              <Filter className="h-4 w-4" /> Filters
            </button>
            <button data-testid="add-user-btn" onClick={() => setAddOpen(true)} className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390]">
              <Plus className="h-4 w-4" /> Add User
            </button>
          </div>
        }
      />
      {loadError && (
        <div data-testid="users-load-error" className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#e4002b]/20 bg-[#e4002b]/5 px-4 py-3">
          <p className="text-sm font-semibold text-[#e4002b]">Couldn't load live user data — nothing is shown from cache or placeholders.</p>
          <button data-testid="users-retry-btn" onClick={() => { setLoadError(false); loadUsers(); loadStats(); }}
            className="shrink-0 rounded-lg bg-[#e4002b] px-4 py-1.5 text-xs font-bold text-white hover:bg-[#b80022]">Retry</button>
        </div>
      )}

      <div className="space-y-5 p-8">
        {/* Search */}
        <div className="flex items-center gap-2 rounded-2xl border border-[#12142b]/8 bg-white px-4 shadow-sm">
          <Search className="h-5 w-5 text-[#565b73]" />
          <input
            data-testid="user-search"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search by name, phone, email, user ID, referral code…"
            className="w-full bg-transparent py-4 text-sm outline-none"
          />
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {statCards.map((c) => {
            const clickable = c.key === "pending";
            const activeFilter = clickable && filters.verification === "pending";
            return (
            <div
              key={c.key}
              data-testid={`ustat-${c.key}`}
              onClick={clickable ? togglePendingFilter : undefined}
              role={clickable ? "button" : undefined}
              className={`rounded-2xl border bg-white p-5 shadow-sm transition-all ${clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : ""} ${activeFilter ? "border-[#f59e0b] ring-2 ring-[#f59e0b]/30" : "border-[#12142b]/8"}`}
            >
              <div className="flex items-start justify-between">
                <p className="text-sm font-medium text-[#565b73]">{c.label}</p>
                <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ backgroundColor: `${c.color}14` }}>
                  <c.icon className="h-4 w-4" strokeWidth={2} style={{ color: c.color }} />
                </span>
              </div>
              <p className="mt-3 font-display text-2xl font-black text-[#12142b]">
                {stats ? (stats[c.key] ?? 0).toLocaleString() : "—"}
              </p>
              <p className="text-xs" style={{ color: c.key === "online" ? "#16a34a" : "#565b73" }}>
                {clickable ? (activeFilter ? "Showing pending · tap to clear" : "Tap to review") : (c.pct && stats ? stats[c.pct] : c.sub)}
              </p>
            </div>
            );
          })}
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-[#12142b]/8 bg-white p-4 shadow-sm">
            {[
              ["Role", "role", [["", "All Roles"], ["rider", "Riders"], ["driver", "Drivers"], ["business", "Business"], ["admin", "Admins"]]],
              ["Status", "status", [["", "All Statuses"], ["active", "Active"], ["pending", "Pending"], ["suspended", "Suspended"], ["banned", "Banned"], ["frozen", "Frozen"]]],
              ["Verification", "verification", [["", "All"], ["verified", "Verified"], ["unverified", "Unverified"], ["pending", "Pending"]]],
              ["Province", "province", [["", "All"], ["AB", "Alberta"], ["SK", "Saskatchewan"], ["MB", "Manitoba"]]],
              ["Joined", "joined", [["", "All Time"], ["24h", "Last 24h"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["90d", "Last 90 days"], ["year", "Last year"]]],
            ].filter(([, key]) => !live || key !== "province").map(([label, key, opts]) => (
              <div key={key} className="min-w-[140px] flex-1">
                <label className="mb-1 block text-xs font-semibold text-[#565b73]">{label}</label>
                <select data-testid={`filter-${key}`} value={filters[key]} onChange={setFilter(key)} className={`${sel} w-full`}>
                  {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
            <button data-testid="clear-filters" onClick={clearFilters} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73] hover:bg-[#f4f6fb]">
              Clear Filters
            </button>
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl border border-[#12142b]/8 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-[#12142b]/8 p-5">
            <h2 className="font-display text-lg font-bold text-[#12142b]">Users ({data.total.toLocaleString()})</h2>
            {selected.length > 0 && (
              <div className="flex items-center gap-2 text-sm" data-testid="bulk-actions-bar">
                <span className="text-[#565b73]" data-testid="bulk-selected-count">{selected.length} selected</span>
                <button data-testid="bulk-suspend" onClick={() => bulk("suspended", "suspended")} className="rounded-lg bg-[#f59e0b]/10 px-3 py-1.5 font-semibold text-[#b45309]">Suspend</button>
                <button data-testid="bulk-ban" onClick={() => bulk("banned", "banned")} className="rounded-lg bg-[#e4002b]/10 px-3 py-1.5 font-semibold text-[#e4002b]">Ban</button>
                <button data-testid="bulk-activate" onClick={() => bulk("active", "reactivated")} className="rounded-lg bg-[#16a34a]/10 px-3 py-1.5 font-semibold text-[#16a34a]">Activate</button>
                <button data-testid="bulk-mark-test" onClick={() => bulkTest(true)} title="Hide selected users (and their rides/finance) from all stats"
                  className="inline-flex items-center gap-1 rounded-lg bg-[#f59e0b]/15 px-3 py-1.5 font-semibold text-[#b45309]"><FlaskConical className="h-3.5 w-3.5" /> Mark as test</button>
                {showTest && (
                  <button data-testid="bulk-unmark-test" onClick={() => bulkTest(false)} title="Return selected test accounts to production stats"
                    className="inline-flex items-center gap-1 rounded-lg border border-[#f59e0b]/40 px-3 py-1.5 font-semibold text-[#b45309]"><FlaskConical className="h-3.5 w-3.5" /> Unmark test</button>
                )}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#12142b]/8 text-xs uppercase tracking-wide text-[#565b73]">
                  <th className="p-4"><input type="checkbox" checked={selected.length === data.items.length && data.items.length > 0} onChange={toggleAll} /></th>
                  <th className="p-4 font-semibold">User</th>
                  <th className="p-4 font-semibold">Role</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold">Verification</th>
                  <th className="p-4 font-semibold">Trips</th>
                  {!live && <th className="p-4 font-semibold">Rating</th>}
                  {!live && <th className="p-4 font-semibold">Wallet / Earnings</th>}
                  <th className="p-4 font-semibold">Joined</th>
                  {!live && <th className="p-4 font-semibold">Last Active</th>}
                  <th className="p-4 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => {
                  const rs = ROLE_STYLE[u.role] || ROLE_STYLE.rider;
                  const isNewVerif = u.pending_verification && !isSeen("verifications", u.id);
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-b border-[#12142b]/5 last:border-0 hover:bg-[#f9fafd] ${isNewVerif ? "bg-[#f59e0b]/6" : ""}`}>
                      <td className={`p-4 ${isNewVerif ? "border-l-2 border-[#f59e0b]" : ""}`}><input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggleSel(u.id)} data-testid={`select-${u.id}`} /></td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <UserAvatar name={u.name} photo={u.photo} testId={`user-avatar-${u.id}`} />
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="font-semibold text-[#12142b]">{u.name}</span>
                              {u.verified_badge && <CheckCircle2 className="h-3.5 w-3.5 text-[#2340b5]" />}
                              {u.is_test && (
                                <span data-testid={`test-badge-${u.id}`} className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-[#f59e0b]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#b45309]"><FlaskConical className="h-3 w-3" /> Test</span>
                              )}
                              {isNewVerif && (
                                <span data-testid={`verif-new-${u.id}`} className="ml-1 inline-flex items-center rounded-full bg-[#f59e0b] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">New</span>
                              )}
                            </div>
                            <p className="text-xs text-[#565b73]">{u.user_code}</p>
                            <p className="text-xs text-[#565b73]">{u.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="rounded-lg px-2.5 py-1 text-xs font-semibold" style={{ color: rs.color, backgroundColor: rs.bg }}>{rs.label}</span>
                      </td>
                      <td className="p-4">
                        <StatusCell status={u.status} online={u.online} />
                        {u.is_frozen && (
                          <span data-testid={`frozen-badge-${u.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#38bdf8]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0369a1]"><Snowflake className="h-3 w-3" /> Frozen</span>
                        )}
                      </td>
                      <td className="p-4"><VerificationCell verification={u.verification} /></td>
                      <td className="p-4">
                        <p className="text-xs text-[#565b73]">Completed <span className="font-semibold text-[#12142b]">{u.trips_completed}</span></p>
                        {!live && <p className="text-xs text-[#565b73]">Upcoming <span className="font-semibold text-[#12142b]">{u.trips_upcoming}</span></p>}
                      </td>
                      {!live && <td className="p-4"><Stars rating={u.rating} count={u.rating_count} /></td>}
                      {!live && (
                        <td className="p-4">
                          <p className="font-semibold text-[#12142b]">{money(u.wallet)}</p>
                          <p className="text-xs text-[#565b73]">{u.earnings ? money(u.earnings) : "—"}</p>
                        </td>
                      )}
                      <td className="p-4 text-[#565b73]">{new Date(u.joined_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" })}</td>
                      {!live && (
                        <td className="p-4">
                          <span className={u.last_active_min <= 60 ? "text-[#16a34a]" : "text-[#565b73]"}>{timeAgo(u.last_active_min)}</span>
                        </td>
                      )}
                      <td className="p-4 text-right">
                        <UserActionsMenu user={u} onChanged={refresh} onView={setProfileUser} onNote={setNoteUser} onMessage={setMessageUser} onKyc={openKyc} onTrips={setTripsUser} onWallet={setWalletUser} />
                      </td>
                    </tr>
                  );
                })}
                {data.items.length === 0 && (
                  <tr><td colSpan={colCount} className="py-12 text-center text-[#565b73]">{loadError ? "" : "No users match your filters."}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / pagination */}
          <div className="flex flex-col items-center justify-between gap-4 border-t border-[#12142b]/8 p-4 text-sm text-[#565b73] sm:flex-row">
            <span>Showing {(data.page - 1) * limit + (data.items.length ? 1 : 0)} to {(data.page - 1) * limit + data.items.length} of {data.total.toLocaleString()} users</span>
            <div className="flex items-center gap-2">
              <button disabled={data.page <= 1} onClick={() => setPage(data.page - 1)} className="rounded-lg border border-[#12142b]/10 px-3 py-1.5 font-medium disabled:opacity-40" data-testid="prev-page">‹</button>
              {pageNumbers().map((n) => (
                <button key={n} onClick={() => setPage(n)} className={`h-8 min-w-[32px] rounded-lg px-2 font-semibold ${n === data.page ? "bg-[#2340b5] text-white" : "border border-[#12142b]/10 text-[#12142b]"}`}>{n}</button>
              ))}
              {data.pages > data.page + 1 && <span>…</span>}
              {data.pages > data.page + 1 && <span className="text-[#12142b]">{data.pages.toLocaleString()}</span>}
              <button disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)} className="rounded-lg border border-[#12142b]/10 px-3 py-1.5 font-medium disabled:opacity-40" data-testid="next-page">›</button>
              <select value={limit} onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }} className={sel} data-testid="page-size">
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      <UserProfileDialog user={profileUser} onClose={() => setProfileUser(null)} />
      <UserTripsDialog user={tripsUser} onClose={() => setTripsUser(null)} />
      <UserWalletDialog user={walletUser} onClose={() => setWalletUser(null)} />
      <AddNoteDialog user={noteUser} onClose={() => setNoteUser(null)} onSaved={refresh} />
      <SendMessageDialog user={messageUser} onClose={() => setMessageUser(null)} />
      <KycDialog user={kycUser} onClose={() => setKycUser(null)} onChanged={refresh} />
      <AddUserDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={refresh} />
    </div>
  );
}
